/**
 * Leitura automática da página via DOM (não imagem / não “previsão”).
 * Descobre hero, KPIs, painéis, tabelas e listas — sem config por rota.
 */

export type TourRegionKind = 'hero' | 'kpis' | 'cards' | 'card' | 'sim' | 'other';

export type TourRegion = {
  id: string;
  kind: TourRegionKind;
  title: string;
  selector: string;
  summary?: string;
  /** JPEG compacto do bloco (só no regenerate / visão IA) */
  screenshotBase64?: string;
  /** HTML compacto do bloco (texto estruturado para a IA) */
  htmlSnippet?: string;
};

const MAX_REGIONS = 28;
const MAX_CONTROLS = 14;

function clean(text: string): string {
  return text.replace(/\s+/g, ' ').trim().slice(0, 120);
}

function slug(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}

function isIgnored(node: Element): boolean {
  return !!node.closest(
    '[data-tour-ignore], [data-sim-chrome], .ecad-help-dialog, .ecad-walkthrough, [role="dialog"], dialog',
  );
}

function isNav(node: Element): boolean {
  return !!node.closest(
    'aside, nav[data-sidebar], [data-slot="sidebar"], .sidebar, [class*="Sidebar"]',
  );
}

function visibleBox(el: HTMLElement): { w: number; h: number; ok: boolean } {
  const r = el.getBoundingClientRect();
  return { w: r.width, h: r.height, ok: r.width >= 180 && r.height >= 60 };
}

/** Painel visual: fundo/borda/raio — funciona com style inline (Master analytics / gestão). */
function looksLikePanel(el: HTMLElement): boolean {
  try {
    const s = window.getComputedStyle(el);
    const bg = s.backgroundColor || '';
    const transparent =
      !bg ||
      bg === 'transparent' ||
      bg === 'rgba(0, 0, 0, 0)' ||
      bg === 'rgba(0,0,0,0)';
    const radius = parseFloat(s.borderRadius) || 0;
    const border =
      parseFloat(s.borderTopWidth || '0') > 0 ||
      (s.borderTop && s.borderTop !== 'none' && !s.borderTop.startsWith('0'));
    const pad =
      parseFloat(s.paddingTop || '0') + parseFloat(s.paddingBottom || '0') >= 12;
    if (radius >= 8 && (!transparent || border) && pad) return true;
    if (el.classList.contains('master-escola-card')) return true;
    if (/\b(card|panel|tile|admin-page)\b/i.test(el.className)) return true;
    // banner com gradiente inline (páginas de gestão)
    const inlineBg = el.style?.background || '';
    if (/gradient|linear-gradient/i.test(inlineBg) && radius >= 8) return true;
    return false;
  } catch {
    return /\b(card|panel)\b/i.test(el.className || '');
  }
}

function findPanelRoot(heading: HTMLElement, scope: Element): HTMLElement | null {
  let el: HTMLElement | null = heading.parentElement;
  let best: HTMLElement | null = null;
  while (el && el !== scope && scope.contains(el)) {
    if (looksLikePanel(el)) {
      const box = visibleBox(el);
      if (box.ok) best = el;
    }
    el = el.parentElement;
  }
  if (!best) {
    el = heading.parentElement;
    while (el && el !== scope) {
      const box = visibleBox(el);
      if (box.w >= 240 && box.h >= 80) {
        best = el;
        break;
      }
      el = el.parentElement;
    }
  }
  return best;
}

/** Sobe a partir do h1 até o banner/hero (gradiente ou painel). */
function findHeroFromH1(h1: HTMLElement, scope: Element): HTMLElement {
  let el: HTMLElement | null = h1;
  let best: HTMLElement = h1;
  while (el && el !== scope && scope.contains(el)) {
    const box = visibleBox(el);
    if (box.ok && looksLikePanel(el)) {
      best = el;
      break;
    }
    // ancestral com altura típica de hero e largura ampla
    if (box.w >= 400 && box.h >= 90 && box.h <= 420) {
      best = el;
    }
    el = el.parentElement;
  }
  return best;
}

function ensureTourId(el: HTMLElement, preferred: string): string {
  const existing = el.getAttribute('data-tour');
  // Reaproveita âncoras manuais (ex.: dash.master.hero); ids auto page.* podem ser trocados
  if (existing && !existing.startsWith('page.')) return existing;
  el.setAttribute('data-tour', preferred);
  return preferred;
}

/** Limpa ids auto da captura anterior (evita spotlight errado no regenerate). */
function clearAutoTourIds(scope: Element) {
  scope.querySelectorAll('[data-tour^="page."]').forEach((el) => {
    el.removeAttribute('data-tour');
  });
}

function panelSummary(el: HTMLElement, title: string): string {
  const p = clean(el.querySelector('p')?.textContent || '');
  if (p && p !== title) return p.slice(0, 160);
  const lines = (el.innerText || '')
    .split('\n')
    .map((l) => clean(l))
    .filter((l) => l && l !== title && l.length >= 4 && l.length <= 80);
  return (lines[1] || lines[0] || title).slice(0, 160);
}

/** Controles pequenos (botão / input) — bbox mais permissivo */
function visibleControl(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  if (r.width < 14 || r.height < 14) return false;
  try {
    const s = window.getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden' || s.opacity === '0') {
      return false;
    }
  } catch {
    /* ignore */
  }
  return r.bottom > 0 && r.right > 0;
}

function controlLabel(el: HTMLElement): string {
  const labelledBy = el.getAttribute('aria-labelledby');
  let fromLabel = '';
  if (labelledBy) {
    const lab = document.getElementById(labelledBy);
    if (lab) fromLabel = clean(lab.textContent || '');
  }
  const id = el.getAttribute('id');
  if (!fromLabel && id) {
    try {
      const safe =
        typeof CSS !== 'undefined' && CSS.escape
          ? CSS.escape(id)
          : id.replace(/"/g, '\\"');
      const lab = document.querySelector(`label[for="${safe}"]`);
      if (lab) fromLabel = clean(lab.textContent || '');
    } catch {
      /* ignore */
    }
  }
  return clean(
    fromLabel ||
      el.getAttribute('aria-label') ||
      el.getAttribute('title') ||
      el.getAttribute('placeholder') ||
      el.getAttribute('name') ||
      el.getAttribute('data-label') ||
      el.textContent ||
      '',
  );
}

function controlKindLabel(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  const role = (el.getAttribute('role') || '').toLowerCase();
  const type = (el.getAttribute('type') || '').toLowerCase();
  if (role === 'tab' || /tab/i.test(el.className)) return 'Aba';
  if (role === 'switch') return 'Interruptor';
  if (type === 'checkbox' || role === 'checkbox') return 'Checkbox';
  if (type === 'radio' || role === 'radio') return 'Opção';
  if (type === 'range' || role === 'slider') return 'Slider';
  if (tag === 'select' || role === 'listbox' || role === 'combobox') {
    return 'Lista / dropdown';
  }
  if (tag === 'textarea') return 'Texto longo';
  if (tag === 'input') {
    if (type === 'search' || /buscar|pesquis/i.test(controlLabel(el))) {
      return 'Busca';
    }
    return 'Campo';
  }
  if (tag === 'a') return 'Link';
  if (tag === 'button' || role === 'button') return 'Botão';
  if (role === 'listitem') return 'Item da lista';
  if (/qr|qrcode/i.test(`${el.className} ${el.id} ${controlLabel(el)}`)) {
    return 'QR Code';
  }
  return 'Controle';
}

function dedupeKey(el: HTMLElement, title: string): string {
  const kind = controlKindLabel(el);
  const type = (el.getAttribute('type') || '').toLowerCase();
  const name = el.getAttribute('name') || '';
  // mesma ação repetida em várias linhas → uma vez
  return `${kind}|${type}|${name}|${title}`.toLowerCase().slice(0, 80);
}

const INTERACTIVE_SEL = [
  'button',
  'a[href]',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  '[role="button"]',
  '[role="tab"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="slider"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="menuitem"]',
  '[role="listitem"]',
  '[contenteditable="true"]',
  'canvas[aria-label*="QR" i], img[alt*="QR" i], [data-qr], [class*="qrcode" i], [class*="qr-code" i]',
].join(',');

/**
 * Extrai regiões lendo o DOM da página (automático, qualquer rota).
 */
export function extractPageRegions(doc: Document = document): TourRegion[] {
  const scope =
    (doc.querySelector(
      'main, .admin-page, .page-content, [role="main"]',
    ) as HTMLElement | null) || doc.body;

  clearAutoTourIds(scope);

  const regions: TourRegion[] = [];
  const usedEls = new Set<HTMLElement>();
  /** Controles aninhados (CTA/busca/ações) podem coexistir com hero/tabela */
  const nestedOk = new Set<HTMLElement>();

  const pushRegion = (
    el: HTMLElement,
    kind: TourRegionKind,
    title: string,
    idHint: string,
    opts?: { nest?: boolean },
  ) => {
    if (usedEls.has(el)) return;
    const nest = !!opts?.nest;

    if (!nest) {
      for (const other of usedEls) {
        if (nestedOk.has(other)) continue;
        if (other.contains(el) || el.contains(other)) {
          const a = el.getBoundingClientRect();
          const b = other.getBoundingClientRect();
          if (a.width * a.height >= b.width * b.height && el.contains(other)) {
            return;
          }
        }
      }
      for (const other of [...usedEls]) {
        if (nestedOk.has(other)) continue;
        if (other.contains(el) && other !== el) {
          usedEls.delete(other);
          const oid = other.getAttribute('data-tour');
          if (oid) {
            const ri = regions.findIndex(
              (r) => r.id === oid || r.selector === `[data-tour="${oid}"]`,
            );
            if (ri >= 0) regions.splice(ri, 1);
          }
        }
      }
    } else if (usedEls.has(el)) {
      return;
    }

    const id = ensureTourId(el, idHint);
    usedEls.add(el);
    if (nest) nestedOk.add(el);
    regions.push({
      id,
      kind,
      title,
      selector: `[data-tour="${id}"]`,
      summary: panelSummary(el, title),
    });
  };

  // 1) Hero — data-tour / header / h1 em banner
  let hero: HTMLElement | null =
    scope.querySelector<HTMLElement>(
      '[data-tour="dash.master.hero"], [data-tour="page.hero"], .master-hero, header.master-hero, .page-hero',
    ) || scope.querySelector<HTMLElement>(':scope > header, header');

  const h1 = scope.querySelector<HTMLElement>('h1');
  if (!hero && h1 && !isNav(h1) && !isIgnored(h1)) {
    hero = findHeroFromH1(h1, scope);
  }
  if (hero && !isNav(hero) && !isIgnored(hero)) {
    const title =
      clean(hero.querySelector('h1')?.textContent || h1?.textContent || '') ||
      'Topo da página';
    pushRegion(hero, 'hero', title, 'page.hero');
  }

  // 2) KPIs (faixa)
  const kpis = scope.querySelector<HTMLElement>(
    '[data-tour="dash.master.kpis"], [data-tour="page.kpis"], .master-hero-stats, [aria-label*="Indicadores"]',
  );
  if (kpis && !isIgnored(kpis) && (!hero || !kpis.contains(hero))) {
    const labels = Array.from(
      kpis.querySelectorAll('.master-hero-stat-label, [class*="stat-label"]'),
    )
      .map((n) => clean(n.textContent || ''))
      .filter(Boolean);
    pushRegion(
      kpis,
      'kpis',
      clean(kpis.getAttribute('aria-label') || 'Indicadores'),
      'page.kpis',
    );
    if (labels.length && regions.length) {
      const last = regions[regions.length - 1];
      if (last.kind === 'kpis') last.summary = labels.join(', ');
    }
  }

  // 3) Painéis por h2/h3
  const headings = Array.from(
    scope.querySelectorAll<HTMLElement>('h2, h3'),
  ).filter((h) => {
    if (isIgnored(h) || isNav(h)) return false;
    if (hero?.contains(h)) return false;
    if (kpis?.contains(h)) return false;
    const t = clean(h.textContent || '');
    return t.length >= 3 && t.length <= 100;
  });

  let autoIdx = 0;
  for (const heading of headings) {
    if (regions.length >= MAX_REGIONS) break;
    const panel = findPanelRoot(heading, scope);
    if (!panel || isIgnored(panel) || isNav(panel)) continue;
    if (hero && (panel === hero || hero.contains(panel))) continue;
    if (kpis && (panel === kpis || kpis.contains(panel))) continue;

    const box = visibleBox(panel);
    if (!box.ok) continue;

    const title = clean(heading.textContent || '') || `Bloco ${autoIdx + 1}`;
    const schoolCards = panel.querySelectorAll<HTMLElement>('.master-escola-card');
    if (
      schoolCards.length >= 1 &&
      panel.classList.contains('master-escolas-panel')
    ) {
      schoolCards.forEach((card, i) => {
        if (regions.length >= MAX_REGIONS) return;
        const ct =
          clean(
            card.querySelector('h3, h4, [class*="nome"], strong')?.textContent ||
              '',
          ) || cardTitleFallback(card, i);
        pushRegion(
          card,
          'card',
          ct,
          `page.card.${++autoIdx}.${slug(ct) || 'escola'}`,
        );
      });
      continue;
    }

    if (/escolas cadastradas|últimas escolas/i.test(title)) {
      const siblingCards = scope.querySelectorAll<HTMLElement>('.master-escola-card');
      if (siblingCards.length) {
        siblingCards.forEach((card, i) => {
          if (regions.length >= MAX_REGIONS) return;
          const ct = cardTitleFallback(card, i);
          pushRegion(
            card,
            'card',
            ct,
            `page.card.${++autoIdx}.${slug(ct) || 'escola'}`,
          );
        });
        continue;
      }
    }

    autoIdx += 1;
    pushRegion(panel, 'card', title, `page.card.${autoIdx}.${slug(title) || 'bloco'}`);
  }

  // 4) Cards de escola restantes
  scope.querySelectorAll<HTMLElement>('.master-escola-card').forEach((card, i) => {
    if (regions.length >= MAX_REGIONS) return;
    if (usedEls.has(card)) return;
    const ct = cardTitleFallback(card, i);
    pushRegion(card, 'card', ct, `page.card.${++autoIdx}.${slug(ct) || 'escola'}`);
  });

  // 5) Tabelas / DataTable (páginas de gestão: Redes, Estados, etc.)
  scope.querySelectorAll<HTMLElement>('table').forEach((table) => {
    if (regions.length >= MAX_REGIONS) return;
    if (isIgnored(table) || isNav(table)) return;
    if (hero?.contains(table) || kpis?.contains(table)) return;

    // bloco = painel que envolve a tabela (borda/raio) ou ancestral com toolbar
    let panel: HTMLElement | null = table.parentElement;
    let found: HTMLElement | null = null;
    while (panel && panel !== scope) {
      if (usedEls.has(panel)) return;
      if (looksLikePanel(panel) || panel.querySelector('table') === table) {
        const box = visibleBox(panel);
        if (box.w >= 280 && box.h >= 100) found = panel;
      }
      // para no primeiro painel visual bom
      if (found && looksLikePanel(found)) break;
      panel = panel.parentElement;
    }
    const target = found || table;
    if (usedEls.has(target)) return;
    if ([...usedEls].some((u) => u.contains(target) || target.contains(u))) {
      // se já está dentro de um card capturado, pula
      if ([...usedEls].some((u) => u.contains(target) && u !== target)) return;
    }

    const toolbarTitle = clean(
      target.querySelector(
        ':scope > div span, :scope > header span, [class*="toolbar"] span',
      )?.textContent || '',
    );
    const ths = Array.from(table.querySelectorAll('thead th'))
      .map((th) => clean(th.textContent || ''))
      .filter(Boolean)
      .slice(0, 5);
    const title =
      (toolbarTitle.length >= 3 && toolbarTitle.length <= 80
        ? toolbarTitle
        : '') ||
      (ths.length ? `Tabela: ${ths.join(', ')}` : '') ||
      'Lista / tabela';

    autoIdx += 1;
    pushRegion(
      target,
      'card',
      title,
      `page.card.${autoIdx}.${slug(title) || 'tabela'}`,
    );
  });

  // 6) Tabs / chips de filtro (ex.: Todos, ADMIN, PROFESSOR…) — UM bloco só
  const tabLists = Array.from(
    scope.querySelectorAll<HTMLElement>(
      '[role="tablist"], [data-slot="tabs-list"], .tabs-list, [class*="TabsList"]',
    ),
  );
  for (const list of tabLists) {
    if (regions.length >= MAX_REGIONS) break;
    if (isIgnored(list) || isNav(list) || !visibleControl(list)) continue;
    const names = Array.from(
      list.querySelectorAll('[role="tab"], button, a'),
    )
      .map((t) => clean(t.textContent || ''))
      .filter((t) => t.length >= 1 && t.length <= 40)
      .slice(0, 8);
    const title = names.length ? `Abas: ${names.join(', ')}` : 'Abas';
    autoIdx += 1;
    pushRegion(
      list,
      'card',
      title,
      `page.tabs.${autoIdx}.${slug(title) || 'abas'}`,
      { nest: true },
    );
  }

  // Chip bars / filtros de perfil: exige texto com "(N)" — NÃO confundir com ícones de ação
  const chipBarParents = new Set<HTMLElement>();
  scope.querySelectorAll<HTMLElement>('button').forEach((btn) => {
    if (isIgnored(btn) || isNav(btn)) return;
    // nunca tratar célula de ação da tabela como filtro
    if (btn.closest('td, [role="cell"]')) return;
    const parent = btn.parentElement as HTMLElement | null;
    if (!parent || chipBarParents.has(parent)) return;
    if (parent.closest('td, tbody, [role="row"]')) return;
    const siblings = Array.from(parent.children).filter(
      (c) => c instanceof HTMLElement && c.tagName === 'BUTTON',
    ) as HTMLElement[];
    if (siblings.length < 3) return;
    const withCount = siblings.filter((b) =>
      /\(\d+\)/.test(clean(b.textContent || '')),
    );
    // Obrigatório: pelo menos 2 chips "Label (n)" (ex.: Todos (974), ADMIN (1))
    if (withCount.length < 2) return;
    chipBarParents.add(parent);
  });
  for (const bar of chipBarParents) {
    if (regions.length >= MAX_REGIONS) break;
    if (usedEls.has(bar) || !visibleControl(bar)) continue;
    const names = Array.from(bar.querySelectorAll(':scope > button'))
      .map((b) => clean(b.textContent || ''))
      .filter(Boolean)
      .slice(0, 10);
    if (names.length < 2) continue;
    const title = `Filtros: ${names.slice(0, 5).join(', ')}${names.length > 5 ? '…' : ''}`;
    autoIdx += 1;
    pushRegion(
      bar,
      'card',
      title,
      `page.filters.${autoIdx}.${slug(names[0] || 'filtros')}`,
      { nest: true },
    );
  }

  // 7) Tabela: ações UMA VEZ só (não repetir por linha / não destacar cada tr)
  let tableActionsDone = false;
  scope.querySelectorAll<HTMLElement>('tbody tr').forEach((row) => {
    if (tableActionsDone || regions.length >= MAX_REGIONS) return;
    if (isIgnored(row) || isNav(row)) return;
    const cells = Array.from(
      row.querySelectorAll<HTMLElement>(':scope > td'),
    );
    if (cells.length < 1) return;

    const actionCell =
      [...cells]
        .reverse()
        .find(
          (td) =>
            td.querySelectorAll('button, .action-btn, [role="button"]').length >
            0,
        ) || null;
    if (!actionCell) return;

    const actionBtns = Array.from(
      actionCell.querySelectorAll<HTMLElement>(
        'button, .action-btn, [role="button"]',
      ),
    ).filter((b) => visibleControl(b));
    if (!actionBtns.length) return;

    const wrap =
      (actionCell.querySelector(':scope > div') as HTMLElement | null) ||
      actionCell;
    if (usedEls.has(wrap)) return;

    const titles = actionBtns
      .map((b) => controlLabel(b))
      .filter((t) => t && t.length > 0 && t.toLowerCase() !== 'botão')
      .slice(0, 5);

    pushRegion(
      wrap,
      'card',
      titles.length
        ? `Ações da tabela: ${titles.join(', ')}`
        : 'Ações da tabela (cada linha)',
      'page.table-actions.once',
      { nest: true },
    );
    // marca tbody inteiro como “já explicado” para não capturar botões de outras linhas
    const tbody = row.closest('tbody');
    if (tbody) nestedOk.add(tbody as HTMLElement);
    tableActionsDone = true;
  });

  // 8) Controles em geral (botão, link, input, select, checkbox, radio, slider, QR…)
  const seenControlKeys = new Set<string>();
  let controlIdx = 0;
  const interactives = Array.from(
    scope.querySelectorAll<HTMLElement>(INTERACTIVE_SEL),
  ).filter((el) => {
    if (isIgnored(el) || isNav(el) || !visibleControl(el)) return false;
    if (usedEls.has(el)) return false;
    // dentro de chip bar / tabs / row / row-actions já capturados
    if ([...nestedOk].some((n) => n.contains(el) && n !== el)) return false;
    if (
      el.getAttribute('role') === 'tab' &&
      el.closest('[role="tablist"], [data-slot="tabs-list"]')
    ) {
      return false;
    }
    // mailto / tel — ruído em listas
    if (el.tagName === 'A') {
      const href = (el.getAttribute('href') || '').toLowerCase();
      if (href.startsWith('mailto:') || href.startsWith('tel:')) return false;
    }
    // paginação fica como um grupo (abaixo), não cada botão
    const label = controlLabel(el).toLowerCase();
    if (/^(anterior|próximo|proximo|next|previous)$/i.test(label)) {
      return false;
    }
    return true;
  });

  // ordena topo→baixo para o tour “parar” na ordem visual
  interactives.sort((a, b) => {
    const ra = a.getBoundingClientRect();
    const rb = b.getBoundingClientRect();
    if (Math.abs(ra.top - rb.top) > 12) return ra.top - rb.top;
    return ra.left - rb.left;
  });

  for (const el of interactives) {
    if (regions.length >= MAX_REGIONS || controlIdx >= MAX_CONTROLS) break;

    // agrupa radio/checkbox do mesmo name no fieldset/pai
    const type = (el.getAttribute('type') || '').toLowerCase();
    const role = (el.getAttribute('role') || '').toLowerCase();
    if (type === 'radio' || type === 'checkbox' || role === 'radio' || role === 'checkbox') {
      const name = el.getAttribute('name');
      const group =
        (el.closest('fieldset, [role="group"], [role="radiogroup"]') as HTMLElement | null) ||
        (name
          ? (el.parentElement as HTMLElement | null)
          : null);
      if (group && !usedEls.has(group) && visibleControl(group)) {
        const key = `group|${name || controlLabel(group)}`;
        if (seenControlKeys.has(key)) continue;
        seenControlKeys.add(key);
        controlIdx += 1;
        const title =
          controlLabel(group) ||
          (name ? `Grupo: ${name}` : controlKindLabel(el));
        pushRegion(
          group,
          'card',
          title,
          `page.ctrl.${controlIdx}.${slug(title) || 'grupo'}`,
          { nest: true },
        );
        continue;
      }
    }

    let title = controlLabel(el);
    const kind = controlKindLabel(el);
    if (!title || title.length < 1) title = kind;
    if (title.length > 60) title = title.slice(0, 60);
    // prefixo se o texto não deixa claro o tipo
    if (
      !/botão|aba|busca|campo|link|slider|checkbox|opção|lista|qr/i.test(title) &&
      kind !== 'Botão'
    ) {
      title = `${kind}: ${title}`;
    }

    const key = dedupeKey(el, title);
    if (seenControlKeys.has(key)) continue;
    seenControlKeys.add(key);

    controlIdx += 1;
    pushRegion(
      el,
      'card',
      title,
      `page.ctrl.${controlIdx}.${slug(title) || 'ctrl'}`,
      { nest: true },
    );
  }

  // Paginação (Anterior / Próximo) — um bloco
  if (regions.length < MAX_REGIONS) {
    const nextBtn = Array.from(
      scope.querySelectorAll<HTMLElement>('button'),
    ).find((b) => /^próximo|proximo|next$/i.test(controlLabel(b)));
    const pageWrap = nextBtn?.closest('div') as HTMLElement | null;
    if (
      pageWrap &&
      !usedEls.has(pageWrap) &&
      /página|pagina|anterior|próximo/i.test(pageWrap.innerText || '')
    ) {
      pushRegion(
        pageWrap,
        'card',
        'Paginação da lista',
        'page.pagination.1',
        { nest: true },
      );
    }
  }

  // Fallback: ainda vazio → h1 + primeiro bloco grande útil
  if (regions.length === 0 && h1 && !isIgnored(h1)) {
    const heroEl = findHeroFromH1(h1, scope);
    pushRegion(
      heroEl,
      'hero',
      clean(h1.textContent || '') || 'Topo da página',
      'page.hero',
    );
  }
  if (regions.length <= 1) {
    const candidates = Array.from(
      scope.querySelectorAll<HTMLElement>(
        '.admin-page > div, main > div > div, [class*="DataTable"], table',
      ),
    ).filter((el) => {
      if (usedEls.has(el) || isIgnored(el) || isNav(el)) return false;
      if (hero && (el === hero || hero.contains(el))) return false;
      const box = visibleBox(el);
      return box.w >= 280 && box.h >= 120;
    });
    for (const el of candidates) {
      if (regions.length >= 4) break;
      if ([...usedEls].some((u) => u.contains(el) || el.contains(u))) continue;
      const title =
        clean(
          el.querySelector('span, h2, h3, caption')?.textContent || '',
        ) || 'Conteúdo principal';
      if (title.length < 2) continue;
      autoIdx += 1;
      pushRegion(
        el,
        'card',
        title.slice(0, 80),
        `page.card.${autoIdx}.${slug(title) || 'bloco'}`,
      );
    }
  }

  // Ordenação visual — linha N imediatamente antes de row-actions N
  regions.sort((a, b) => {
    const ea = scope.querySelector<HTMLElement>(a.selector);
    const eb = scope.querySelector<HTMLElement>(b.selector);
    if (!ea || !eb) return 0;

    if (ea.contains(eb) && ea !== eb) return -1;
    if (eb.contains(ea) && ea !== eb) return 1;

    const ra = ea.getBoundingClientRect();
    const rb = eb.getBoundingClientRect();
    if (Math.abs(ra.top - rb.top) > 40) return ra.top - rb.top;
    return ra.left - rb.left;
  });

  const heroes = regions.filter((r) => r.kind === 'hero');
  const kpiRegs = regions.filter((r) => r.kind === 'kpis');
  const rest = regions.filter((r) => r.kind !== 'hero' && r.kind !== 'kpis');
  return [...heroes, ...kpiRegs, ...rest].slice(0, MAX_REGIONS);
}

function cardTitleFallback(el: HTMLElement, idx: number): string {
  const h = clean(
    el.querySelector('h1, h2, h3, h4, strong')?.textContent || '',
  );
  if (h.length >= 3) return h;
  const line = (el.innerText || '')
    .split('\n')
    .map((l) => clean(l))
    .find((l) => l.length >= 3 && l.length <= 90);
  return line || `Card ${idx + 1}`;
}

export function hashRegions(regions: TourRegion[]): string {
  const payload = JSON.stringify(
    regions.map((r) => [r.kind, r.selector, r.title]),
  );
  let h = 0x811c9dc5;
  for (let i = 0; i < payload.length; i++) {
    h ^= payload.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}
